import React, { useState, useMemo } from 'react';
import {
  ChartBarIcon,
  DocumentTextIcon,
  SparklesIcon,
  ClockIcon,
  ArrowUpIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Appointment } from '../../types/appointment';
import { ClinicalCard, AlertBadge, HealthMetric } from './shared';
import { useClinicalInsights } from '../../hooks/usePatientCockpit';
import { formatDate } from '../../utils/dateFormat';

/**
 * Clinical Intelligence - rule-based summaries of the patient's appointment history
 * Provides trend analysis, common patterns, and clinical decision support
 */
interface ClinicalIntelligenceProps {
  appointments: Appointment[];
  patientId: string;
  className?: string;
}

const ClinicalIntelligence: React.FC<ClinicalIntelligenceProps> = ({
  appointments,
  patientId,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'trends' | 'insights'>('overview');
  const clinicalInsights = useClinicalInsights(appointments);

  // Process transcription data for insights
  const transcriptionInsights = useMemo(() => {
    const completedAppointments = appointments.filter(
      apt => apt.transcription.status === 'completed'
    );

    return {
      totalTranscriptions: completedAppointments.length,
      averageConfidence: completedAppointments.reduce(
        (acc, apt) => acc + (apt.transcription.confidence || 0), 0
      ) / (completedAppointments.length || 1),
      totalWords: completedAppointments.reduce(
        (acc, apt) => acc + (apt.transcription.wordCount || 0), 0
      ),
      averageProcessingTime: completedAppointments.reduce(
        (acc, apt) => acc + (apt.transcription.processingTime || 0), 0
      ) / (completedAppointments.length || 1)
    };
  }, [appointments]);

  const tabs = [
    {
      id: 'overview' as const,
      label: 'Overview',
      icon: <ChartBarIcon className="w-4 h-4" />,
      count: clinicalInsights.totalVisits
    },
    {
      id: 'trends' as const,
      label: 'Trends',
      icon: <ArrowUpIcon className="w-4 h-4" />,
      badge: clinicalInsights.hasRecentActivity ? 'Active' : undefined
    },
    {
      id: 'insights' as const,
      label: 'Visit Insights',
      icon: <SparklesIcon className="w-4 h-4" />
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Visit Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <HealthMetric
                label="Total Visits"
                value={clinicalInsights.totalVisits}
                icon={<CalendarIcon className="w-4 h-4" />}
                size="sm"
              />
              <HealthMetric
                label="Last 30 Days"
                value={clinicalInsights.visitsLast30Days}
                icon={<ClockIcon className="w-4 h-4" />}
                status={clinicalInsights.visitsLast30Days > 2 ? 'warning' : 'normal'}
                size="sm"
              />
              <HealthMetric
                label="Avg Interval"
                value={clinicalInsights.averageVisitInterval}
                unit="days"
                icon={<ArrowUpIcon className="w-4 h-4" />}
                size="sm"
              />
              <HealthMetric
                label="Transcribed"
                value={transcriptionInsights.totalTranscriptions}
                icon={<DocumentTextIcon className="w-4 h-4" />}
                status="normal"
                size="sm"
              />
            </div>

            {/* Appointment Type Breakdown */}
            {Object.keys(clinicalInsights.appointmentTypeBreakdown).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Appointment Types</h4>
                <div className="space-y-2">
                  {Object.entries(clinicalInsights.appointmentTypeBreakdown).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600 capitalize">{type}</span>
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${(count / clinicalInsights.totalVisits) * 100}%`
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-8">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Chief Complaints */}
            {clinicalInsights.recentChiefComplaints.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Chief Complaints</h4>
                <div className="space-y-2">
                  {clinicalInsights.recentChiefComplaints.map((complaint, index) => (
                    <div key={index} className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
                      {complaint}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'trends':
        return (
          <div className="space-y-6">
            {/* Visit Frequency Trend */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <ArrowUpIcon className="w-4 h-4 mr-2" />
                Visit Frequency Analysis
              </h4>
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-blue-800">Average between visits</span>
                  <span className="text-lg font-bold text-blue-900">
                    {clinicalInsights.averageVisitInterval} days
                  </span>
                </div>
                <div className="text-xs text-blue-600">
                  {clinicalInsights.averageVisitInterval < 30 
                    ? 'Frequent visits - monitor for chronic conditions'
                    : clinicalInsights.averageVisitInterval > 365
                    ? 'Infrequent visits - consider preventive care outreach'
                    : 'Normal visit pattern'
                  }
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Activity</h4>
              <div className={`p-4 rounded-lg ${
                clinicalInsights.hasRecentActivity ? 'bg-green-50' : 'bg-yellow-50'
              }`}>
                <div className="flex items-center">
                  {clinicalInsights.hasRecentActivity ? (
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-3" />
                  ) : (
                    <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 mr-3" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${
                      clinicalInsights.hasRecentActivity ? 'text-green-800' : 'text-yellow-800'
                    }`}>
                      {clinicalInsights.visitsLast30Days} visits in the last 30 days
                    </p>
                    <p className={`text-xs ${
                      clinicalInsights.hasRecentActivity ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {clinicalInsights.hasRecentActivity 
                        ? 'Patient has recent engagement'
                        : 'Consider follow-up outreach'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Last Visit Info */}
            {clinicalInsights.lastVisitDate && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Last Visit</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">
                    <strong>Date:</strong> {formatDate(clinicalInsights.lastVisitDate)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {Math.floor((new Date().getTime() - new Date(clinicalInsights.lastVisitDate).getTime()) / (1000 * 60 * 60 * 24))} days ago
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'insights':
        return (
          <div className="space-y-6">
            {/* Rule-based visit and transcription summary */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <SparklesIcon className="w-4 h-4 mr-2" />
                Visit Insights
              </h4>
              <p className="text-xs text-gray-500 mb-3">
                Counts and fixed rules from this patient&apos;s visits and transcriptions.
              </p>
              
              {transcriptionInsights.totalTranscriptions > 0 ? (
                <div className="space-y-4">
                  {/* Transcription Quality */}
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-purple-800">Transcription Quality</span>
                      <AlertBadge type="info" size="sm">
                        {Math.round(transcriptionInsights.averageConfidence * 100)}% avg
                      </AlertBadge>
                    </div>
                    <div className="text-xs text-purple-600">
                      Based on {transcriptionInsights.totalTranscriptions} transcribed appointments
                    </div>
                  </div>

                  {/* Communication Patterns */}
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-indigo-800">Communication Volume</span>
                      <span className="text-sm font-medium text-indigo-900">
                        {transcriptionInsights.totalWords.toLocaleString()} words
                      </span>
                    </div>
                    <div className="text-xs text-indigo-600">
                      Average: {Math.round(transcriptionInsights.totalWords / transcriptionInsights.totalTranscriptions)} words per visit
                    </div>
                  </div>

                  {/* Clinical Recommendations */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Recommendations</h5>
                    <div className="space-y-2">
                      {clinicalInsights.visitsLast30Days > 3 && (
                        <div className="flex items-start p-3 bg-yellow-50 rounded-md">
                          <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-yellow-800">High visit frequency detected</p>
                            <p className="text-xs text-yellow-600">Consider care coordination or chronic disease management</p>
                          </div>
                        </div>
                      )}
                      
                      {transcriptionInsights.averageConfidence < 0.8 && (
                        <div className="flex items-start p-3 bg-blue-50 rounded-md">
                          <DocumentTextIcon className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-blue-800">Lower transcription confidence</p>
                            <p className="text-xs text-blue-600">Consider audio quality improvements or manual review</p>
                          </div>
                        </div>
                      )}

                      {clinicalInsights.totalVisits === 1 && (
                        <div className="flex items-start p-3 bg-green-50 rounded-md">
                          <SparklesIcon className="w-4 h-4 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-green-800">New patient</p>
                            <p className="text-xs text-green-600">Ensure comprehensive initial assessment and care plan</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <SparklesIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-2">No transcription data available</p>
                  <p className="text-xs text-gray-400">
                    Insights will appear after appointments are transcribed
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <ClinicalCard
      title="Clinical Intelligence"
      icon={<ChartBarIcon className="w-5 h-5" />}
      className={className}
      size="lg"
    >
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span className="ml-2">{tab.label}</span>
              {tab.count !== undefined && (
                <span className="ml-2 bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
              {tab.badge && (
                <AlertBadge type="info" size="sm" className="ml-2">
                  {tab.badge}
                </AlertBadge>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </ClinicalCard>
  );
};

// Import CalendarIcon for the overview section
import { CalendarIcon } from '@heroicons/react/24/outline';

export default ClinicalIntelligence;